import { DEFAULT_MEALS, NEEDS, NEED_KEYS, type MealGift, type NeedKey } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { number, idFor } from './form';

const pounds = (pence: number) => (pence / 100).toFixed(2);

// Meals: what players can buy each other from a chat (Lagos Life's "Buy them food"): the name and icon, the
// price, and how it fills them up and cheers them up. A meal can come off the menu for a while. Changes are part
// of the content draft, published with it.
export function MealsPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const meals = working?.meals ?? DEFAULT_MEALS;
  // content made before meals were content gets the starting menu the first time it is changed
  const change = (fn: (meals: MealGift[]) => void) =>
    editor.edit((content) => {
      content.meals ??= structuredClone(DEFAULT_MEALS);
      fn(content.meals);
    });
  const edit = (id: string, fn: (meal: MealGift) => void) =>
    change((list) => {
      const meal = list.find((item) => item.id === id);
      if (meal) fn(meal);
    });
  return (
    <section>
      <PageHeader
        title="Meals"
        description="What players can buy each other from a chat: the price, and how it fills them up. It is delivered at once. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          {meals.map((meal) => {
            const key = (name: string) => `${meal.id}-${name}`;
            return (
              <fieldset
                key={meal.id}
                className="admin-panel content-section"
                disabled={readOnly}
                aria-label={meal.label}
              >
                <h2 className="content-subtitle">
                  <span aria-hidden="true">{meal.icon}</span> {meal.label}
                  <span className="admin-muted"> · {meal.id}</span>
                </h2>
                <div className="content-grid">
                  <div className="admin-field">
                    <Label htmlFor={key('label')}>Name</Label>
                    <Input
                      id={key('label')}
                      value={meal.label}
                      maxLength={40}
                      onChange={(e) => edit(meal.id, (item) => void (item.label = e.target.value))}
                    />
                  </div>
                  <div className="admin-field">
                    <Label htmlFor={key('icon')}>Icon</Label>
                    <Input
                      id={key('icon')}
                      value={meal.icon}
                      maxLength={16}
                      onChange={(e) => edit(meal.id, (item) => void (item.icon = e.target.value))}
                    />
                  </div>
                  <div className="admin-field">
                    <Label htmlFor={key('price')}>Price (£)</Label>
                    <Input
                      id={key('price')}
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={pounds(meal.pricePence)}
                      onChange={(e) =>
                        edit(
                          meal.id,
                          (item) =>
                            void (item.pricePence = Math.max(1, Math.round(number(e.target.value, 0.01) * 100))),
                        )
                      }
                    />
                  </div>
                </div>
                <label className="designer-check">
                  <Switch
                    checked={meal.visible}
                    aria-label={`${meal.label} is on the menu`}
                    onCheckedChange={(on) => edit(meal.id, (item) => void (item.visible = on))}
                  />{' '}
                  On the menu
                </label>
                <p className="content-mini-title">What it does to the one who gets it (−100 to 100)</p>
                <div className="content-needs">
                  {NEED_KEYS.map((need: NeedKey) => (
                    <label key={need}>
                      <span>
                        {NEEDS[need].icon} {NEEDS[need].label}
                      </span>
                      <Input
                        type="number"
                        min={-100}
                        max={100}
                        value={meal.effects[need] ?? ''}
                        onChange={(e) =>
                          edit(meal.id, (item) => {
                            const value = Math.round(number(e.target.value, 0));
                            if (value) item.effects[need] = Math.max(-100, Math.min(100, value));
                            else delete item.effects[need];
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
                {!readOnly && (
                  <div className="admin-page-actions">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        change(
                          (list) =>
                            void list.splice(
                              list.findIndex((item) => item.id === meal.id),
                              1,
                            ),
                        )
                      }
                    >
                      Remove {meal.label}
                    </Button>
                  </div>
                )}
              </fieldset>
            );
          })}
          {!readOnly && (
            <div className="admin-page-actions">
              <Button
                type="button"
                onClick={() =>
                  change(
                    (list) =>
                      void list.push({
                        id: idFor('New meal', new Set(list.map((item) => item.id)), 'meal', 30),
                        label: 'New meal',
                        icon: '🍽️',
                        pricePence: 1_000,
                        effects: { hunger: 60, fun: 5 },
                        visible: false,
                      }),
                  )
                }
              >
                Add a meal
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
